import type {
    TcrBrandCreate,
    TcrCampaignCreate,
    TcrCampaignPacket,
} from '~/services/adminTenDlcRegistrationClient.types';

// Minted once when the form opens; TCR rejects a second submission with the same id.
// crypto.randomUUID() needs a secure context: production Kiosk is HTTPS and http://localhost counts as secure.
export const mintReferenceId = (): string => crypto.randomUUID();

// The fields PUT /campaign/{id} accepts, in form order; the edit modal copies exactly these.
export const packetKeys = [
    'description',
    'message_flow',
    'samples',
    'optin_keywords',
    'optin_message',
    'optout_keywords',
    'optout_message',
    'help_keywords',
    'help_message',
    'privacy_policy_link',
    'terms_and_conditions_link',
    'embedded_link',
    'embedded_link_sample',
    'embedded_phone',
    'number_pool',
    'age_gated',
    'direct_lending',
    'auto_renewal',
] as const satisfies readonly (keyof TcrCampaignPacket)[];

export const brandDefaults = (): TcrBrandCreate => ({
    reference_id: mintReferenceId(),
    customer_tag: '',
    entity_type: 'PRIVATE_PROFIT',
    display_name: '',
    company_name: '',
    ein: '',
    ein_issuing_country: 'US',
    country: 'US',
    street: '',
    city: '',
    state: '',
    postal_code: '',
    phone: '',
    email: '',
    website: '',
    vertical: '',
    brand_relationship: 'KEY_ACCOUNT',
    stock_symbol: '',
    stock_exchange: '',
    business_contact_email: '',
    alt_business_id: '',
    alt_business_id_type: '',
});

export const campaignDefaults = (brandId = ''): TcrCampaignCreate => ({
    brand_id: brandId,
    reference_id: mintReferenceId(),
    customer_tag: '',
    usecase: 'MARKETING',
    sub_usecases: [],
    description: '',
    message_flow: '',
    samples: ['', ''],
    subscriber_optin: true,
    optin_keywords: '',
    optin_message: '',
    subscriber_optout: true,
    optout_keywords: 'STOP',
    optout_message: '',
    subscriber_help: true,
    help_keywords: 'HELP',
    help_message: '',
    privacy_policy_link: '',
    terms_and_conditions_link: '',
    embedded_link: true,
    embedded_link_sample: '',
    embedded_phone: false,
    number_pool: false,
    age_gated: false,
    direct_lending: false,
    auto_renewal: true,
    terms_and_conditions: true,
});

// Multipart because the opt-in screenshot rides along; booleans go as 1/0 for Laravel's boolean rule.
export const toCampaignFormData = (form: TcrCampaignCreate, screenshot: File): FormData => {
    const data = new FormData();

    Object.entries(form).forEach(([key, value]) => {
        if (Array.isArray(value)) {
            value.forEach((item, index) => data.append(`${key}[${index}]`, item));
        } else if (typeof value === 'boolean') {
            data.append(key, value ? '1' : '0');
        } else {
            data.append(key, value);
        }
    });

    data.append('optin_screenshot', screenshot, screenshot.name);

    return data;
};
